/*
 * Copyright 2026 Sony Corporation
 */
import { z } from 'zod';

import { PageLayoutSchema } from './layout.js';
import { LogKindSchema, LogLevelSchema } from './logs.js';

/**
 * One thing to do or check in the running app's page, carried out by the device's agent through the app's probe.
 * A target names an element by data-testid, by id, or as "text=<exact label>" for buttons, links and choices. Text checks
 * are case-sensitive substring matches, so "Connected" does not pass for "Not connected".
 */
const target = z
  .string()
  .min(1)
  .max(200)
  .describe(
    'data-testid or id of the element, or "text=<exact label>" of a button, link, tab or option of an open select'
  );
const timeout = z.number().int().min(100).max(60_000).optional().describe('How long to wait, in ms (default 15000).');
const criterion = z
  .string()
  .min(1)
  .max(200)
  .optional()
  .describe(
    'The acceptance criterion this step helps show. When a step fails, the rest of its criterion is skipped and the check goes on with the next one; a step without a criterion is setup, and its failure ends the check.'
  );

export const VerificationStepSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('click'), criterion, target }).describe('Click a button, link or other control.'),
  z
    .object({ action: z.literal('type'), criterion, target, text: z.string().max(500) })
    .describe('Replace the value of an input with text.'),
  z
    .object({
      action: z.literal('wait_for'),
      criterion,
      target: target.optional(),
      text: z.string().min(1).max(300).optional(),
      timeout_ms: timeout,
    })
    .describe('Wait until the element is shown (and contains text, when given), or until the page shows text.'),
  z
    .object({
      action: z.literal('expect'),
      criterion,
      target: target.optional(),
      text: z.string().min(1).max(300).optional(),
      absent: z.boolean().optional().describe('True: the element must not be shown.'),
    })
    .describe('Check now that the element is shown (with text, when given), or that the page shows text.'),
  z
    .object({
      action: z.literal('expect_event'),
      criterion,
      operation: z.string().min(1).max(100),
      text: z.string().min(1).max(300).optional(),
      timeout_ms: timeout,
    })
    .describe(
      'Wait for the app to report an event (report.Event) with this operation since the last click or type; text must be part of its message.'
    ),
  z
    .object({ action: z.literal('expect_no_errors'), criterion })
    .describe('Check that neither the app nor its page reported an error since the check started.'),
  z
    .object({ action: z.literal('snapshot'), criterion, label: z.string().min(1).max(60).optional() })
    .describe('Keep a picture of the page at this point, for people to look at.'),
  z
    .object({ action: z.literal('sleep'), criterion, ms: z.number().int().min(1).max(10_000) })
    .describe('Wait a fixed time.'),
]);
export type VerificationStep = z.infer<typeof VerificationStepSchema>;

export const MAX_VERIFICATION_STEPS = 25;
export const VerificationStepsSchema = z.array(VerificationStepSchema).max(MAX_VERIFICATION_STEPS);

/**
 * `sent` until the device answers. `passed` and `failed` say how the checks went; `error` is a check that could not
 * be carried out (the app is not running, its page did not load).
 */
export const VerificationStateSchema = z.enum(['sent', 'passed', 'failed', 'error']);
export type VerificationState = z.infer<typeof VerificationStateSchema>;

/** Who asked: the assistant checking its work, or a person who wanted to see the screen. */
export const VerificationSourceSchema = z.enum(['assistant', 'user']);

export const StepResultSchema = z.object({
  index: z.number().int().nonnegative(),
  action: z.string().max(40),
  target: z.string().max(200).optional(),
  ok: z.boolean(),
  /** Not carried out, because an earlier step of the same criterion failed. */
  skipped: z.boolean().optional(),
  detail: z.string().max(2000).optional(),
  ms: z.number().int().nonnegative(),
});
export type StepResult = z.infer<typeof StepResultSchema>;

/** How one acceptance criterion of a check went: every step passed, one failed, or it was never carried out. */
export type CriterionOutcome = 'passed' | 'failed' | 'not_run';
export interface CriterionResult {
  criterion: string;
  outcome: CriterionOutcome;
  /** 1-based number of the step that failed, and what it said. */
  failed_step?: number;
  detail?: string;
}

/** The criteria of a check's steps in the order they first appear, each with how it went. */
export const criteriaOf = (
  steps: readonly Pick<VerificationStep, 'criterion'>[],
  results: readonly StepResult[]
): CriterionResult[] => {
  const byIndex = new Map(results.map((r) => [r.index, r]));
  const indexes = new Map<string, number[]>();
  steps.forEach((step, i) => {
    if (step.criterion) {
      indexes.set(step.criterion, [...(indexes.get(step.criterion) ?? []), i]);
    }
  });
  return [...indexes].map(([criterion, of]) => {
    const failed = of.map((i) => byIndex.get(i)).find((r) => r && !r.ok && !r.skipped);
    if (failed) {
      return {
        criterion,
        outcome: 'failed' as const,
        failed_step: failed.index + 1,
        ...(failed.detail ? { detail: failed.detail } : {}),
      };
    }
    return { criterion, outcome: of.every((i) => byIndex.get(i)?.ok) ? ('passed' as const) : ('not_run' as const) };
  });
};

export const PageViewSchema = z.object({
  title: z.string().max(500),
  text: z.string().max(5000),
  elements: z.array(z.record(z.string(), z.unknown())).max(100),
  errors: z.array(z.string().max(1000)).max(50).optional(),
  layout: PageLayoutSchema.optional(),
});
export type PageView = z.infer<typeof PageViewSchema>;

/**
 * The page as markup a browser can show (styles inlined, scripts removed); it must only ever be shown sandboxed. The
 * device uploads the markup to the verification's folder and names it by `object`; markup in the report itself is the
 * fallback when the upload failed.
 */
export const MAX_SNAPSHOT_OBJECTS = 30;
export const MAX_SNAPSHOT_HTML = 110_000;
export const PageSnapshotSchema = z.object({
  label: z.string().max(100),
  html: z.string().max(MAX_SNAPSHOT_HTML).optional(),
  object: z
    .number()
    .int()
    .min(0)
    .max(MAX_SNAPSHOT_OBJECTS - 1)
    .optional(),
  truncated: z.boolean().optional(),
  at: z.string().max(40),
});
export type PageSnapshot = z.infer<typeof PageSnapshotSchema>;

export const VerificationEventSchema = z.object({
  ts: z.string().max(40).optional(),
  level: LogLevelSchema,
  source: z.string().max(20),
  kind: LogKindSchema.or(z.string().max(20)),
  code: z.string().max(10).optional(),
  operation: z.string().max(200).optional(),
  message: z.string().max(4000),
  data: z.record(z.string(), z.unknown()).optional(),
});

/** What the device's agent publishes on `jit/{device_id}/verifications`, plus the device id the IoT rule adds. */
export const VerificationReportEventSchema = z.object({
  device_id: z.string().uuid(),
  verification_id: z.string().min(1).max(100),
  deployment_id: z.string().max(100),
  version: z.number().int().nonnegative(),
  state: z.enum(['passed', 'failed', 'error']),
  error: z.string().max(2000).optional(),
  steps: z.array(StepResultSchema).max(50).default([]),
  page: PageViewSchema.optional(),
  snapshots: z.array(PageSnapshotSchema).max(MAX_SNAPSHOT_OBJECTS).optional(),
  events: z.array(VerificationEventSchema).max(50).optional(),
  /** What the app exposed (pkg/probe Expose) when the steps were done, by name. */
  app_state: z.record(z.string(), z.unknown()).optional(),
  started_at: z.string().max(40),
  finished_at: z.string().max(40),
});
export type VerificationReportEvent = z.infer<typeof VerificationReportEventSchema>;

export const VerificationSchema = z.object({
  project_id: z.string().uuid(),
  verification_id: z.string().min(1),
  /** The version that was checked, and the build it was made from. */
  version: z.number().int().positive(),
  build: z.number().int().positive(),
  device_id: z.string().uuid(),
  source: VerificationSourceSchema,
  run_id: z.string().nullable().default(null),
  steps: VerificationStepsSchema,
  state: VerificationStateSchema,
  error: z.string().nullable().default(null),
  results: z.array(StepResultSchema).default([]),
  page: PageViewSchema.nullable().default(null),
  snapshots: z.array(PageSnapshotSchema).default([]),
  events: z.array(VerificationEventSchema).default([]),
  app_state: z.record(z.string(), z.unknown()).nullable().default(null),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  finished_at: z.string().nullable().default(null),
});
export type Verification = z.infer<typeof VerificationSchema>;

/** A verification in a list: without the markup, which is large. */
export const VerificationSummarySchema = VerificationSchema.omit({ snapshots: true }).extend({
  snapshot_labels: z.array(z.string()).default([]),
});
export type VerificationSummary = z.infer<typeof VerificationSummarySchema>;

export const CreateVerificationRequestSchema = z
  .object({
    /** Nothing to do means only looking: the device sends a picture of the page as it is. */
    steps: VerificationStepsSchema.default([]),
  })
  .strict();
export type CreateVerificationRequest = z.input<typeof CreateVerificationRequestSchema>;

export const ListVerificationsResponseSchema = z.object({ verifications: z.array(VerificationSummarySchema) });
export type ListVerificationsResponse = z.infer<typeof ListVerificationsResponseSchema>;
